import { useEffect } from "react";

import type { TelegramMainButton } from "../../lib/telegram-types.js";

export interface MainButtonConfig {
  visible: boolean;
  text: string;
  enabled?: boolean;
  loading?: boolean;
  onClick?: () => void;
}

/**
 * Drive Telegram's native MainButton from React. The hook owns the
 * button's lifecycle for as long as the component is mounted: it sets
 * the label, toggles visibility, and (un)binds a single click handler.
 *
 * `MainButton` is a singleton on the WebApp; mount this hook in exactly
 * one place at a time.
 */
export function useMainButton(
  button: TelegramMainButton | null,
  config: MainButtonConfig,
): void {
  const { visible, text, enabled = true, loading = false, onClick } = config;

  useEffect(() => {
    if (!button) return;
    if (!visible) {
      button.hide();
      return;
    }
    button.setText(text);
    if (enabled) button.enable();
    else button.disable();
    if (loading) button.showProgress(true);
    else button.hideProgress();
    button.show();
  }, [button, visible, text, enabled, loading]);

  useEffect(() => {
    if (!button || !visible || !onClick) return;
    button.onClick(onClick);
    return () => {
      button.offClick(onClick);
    };
  }, [button, visible, onClick]);

  // Hide the button on unmount so it doesn't bleed into other screens.
  useEffect(() => {
    if (!button) return;
    return () => {
      button.hide();
      button.hideProgress();
    };
  }, [button]);
}
